import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, apiGet } from "../../api";
import { Skeleton, SkeletonStatGrid } from "../../components/Loaders";
import type { HealthStatus, PublicHealthStatus } from "../../types";

interface UpdateStatus {
  manifest_exists: boolean;
  db_exists: boolean;
  entries?: Record<string, { updated_at?: string; path?: string }>;
}

function formatStamp(iso?: string) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function isHealthStatus(data: unknown): data is HealthStatus {
  return (
    typeof data === "object" &&
    data !== null &&
    "ollama" in data &&
    "db_exists" in data
  );
}

function mapPublicHealth(data: PublicHealthStatus): HealthStatus {
  return {
    ollama: {
      reachable: data.ollama_reachable,
      model_installed: data.model_installed ?? undefined,
      configured_model: data.model,
    },
    db_exists: data.db_ready,
    db_path: "",
    model: data.model,
    rulings_loaded: data.rulings_loaded,
    think_enabled: false,
  };
}

async function getAdminHealth(): Promise<{ health: HealthStatus; fallback: boolean }> {
  try {
    return { health: await apiGet<HealthStatus>("/api/admin/health"), fallback: false };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      const publicHealth = await apiGet<PublicHealthStatus | HealthStatus>("/api/health");
      return {
        health: isHealthStatus(publicHealth) ? publicHealth : mapPublicHealth(publicHealth),
        fallback: true,
      };
    }
    throw err;
  }
}

export function AdminOverview() {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [status, setStatus] = useState<UpdateStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [healthFallback, setHealthFallback] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setError(null);
    const [h, s] = await Promise.allSettled([
      getAdminHealth(),
      apiGet<UpdateStatus>("/api/update/status"),
    ]);

    if (h.status === "fulfilled") {
      setHealth(h.value.health);
      setHealthFallback(h.value.fallback);
    }
    else setError((h.reason as Error)?.message ?? "Failed to load health");

    if (s.status === "fulfilled") setStatus(s.value);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    refresh().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const ollamaOk = health?.ollama?.reachable;
  const dbOk = health?.db_exists;
  const manifestOk = status?.manifest_exists;
  const modelInstalled = health?.ollama?.model_installed;
  const noHealth = health == null;
  const manifestEntries = status?.entries
    ? Object.entries(status.entries).sort(([a], [b]) => a.localeCompare(b))
    : [];

  if (loading) {
    return (
      <>
        <h2>Overview</h2>
        <div>
          <SkeletonStatGrid count={3} />
        </div>
        <Skeleton variant="text" width="70%" />
      </>
    );
  }

  return (
    <>
      <h2>Overview</h2>
      {error && <div className="error-banner">{error}</div>}
      {healthFallback && (
        <div className="info-banner">
          Detailed admin health is unavailable on this backend, so this page is
          showing the public health summary.
        </div>
      )}

      <div className="admin-overview-grid">
        <article className="admin-metric-card admin-metric-card--ollama">
          <div className="admin-metric-card__head">
            <h3 className="admin-metric-card__title">Ollama</h3>
            <span
              className={
                noHealth
                  ? "admin-metric-card__badge admin-metric-card__badge--warn"
                  : ollamaOk
                    ? "admin-metric-card__badge admin-metric-card__badge--ok"
                    : "admin-metric-card__badge admin-metric-card__badge--bad"
              }
            >
              {noHealth ? "Unknown" : ollamaOk ? "Reachable" : "Unreachable"}
            </span>
          </div>
          <div className="admin-metric-card__body">
            <div className="admin-metric-card__lead">
              {health?.ollama_url ?? (healthFallback ? "Public health summary" : "—")}
            </div>
            <div className="admin-metric-card__meta">
              <span className="admin-metric-kv">
                <span className="admin-metric-kv__k">Model</span>
                <span className="admin-metric-kv__v">{health?.model ?? "—"}</span>
              </span>
              {health && ollamaOk && (
                <span
                  className={
                    modelInstalled
                      ? "admin-metric-chip admin-metric-chip--ok"
                      : "admin-metric-chip admin-metric-chip--warn"
                  }
                >
                  {modelInstalled ? "Model pulled on host" : "Model not installed on host"}
                </span>
              )}
            </div>
          </div>
        </article>

        <article className="admin-metric-card admin-metric-card--db">
          <div className="admin-metric-card__head">
            <h3 className="admin-metric-card__title">Card database</h3>
            <span
              className={
                noHealth
                  ? "admin-metric-card__badge admin-metric-card__badge--warn"
                  : dbOk
                    ? "admin-metric-card__badge admin-metric-card__badge--ok"
                    : "admin-metric-card__badge admin-metric-card__badge--warn"
              }
            >
              {noHealth ? "Unknown" : dbOk ? "Present" : "Missing"}
            </span>
          </div>
          <div className="admin-metric-card__body">
            <p className="admin-metric-card__lead">
              {!health
                ? "Could not load database status."
                : health.rulings_loaded
                  ? "Rulings table loaded — ready for rules-aware features."
                  : "Rulings not loaded — import rulings for fuller answers."}
            </p>
            <div className="admin-metric-card__meta mono">
              {health?.db_path || (healthFallback ? "Path hidden by public health endpoint" : "—")}
            </div>
          </div>
        </article>

        <article className="admin-metric-card admin-metric-card--manifest">
          <div className="admin-metric-card__head">
            <h3 className="admin-metric-card__title">Bulk manifest</h3>
            <span
              className={
                manifestOk
                  ? "admin-metric-card__badge admin-metric-card__badge--ok"
                  : "admin-metric-card__badge admin-metric-card__badge--warn"
              }
            >
              {manifestOk ? "Synced" : "Not synced"}
            </span>
          </div>
          <div className="admin-metric-card__body">
            {manifestEntries.length === 0 ? (
              <p className="admin-metric-card__lead muted">No bulk types recorded yet.</p>
            ) : (
              <ul className="admin-metric-card__entries">
                {manifestEntries.map(([k, v]) => (
                  <li key={k}>
                    <span>{k}</span>
                    <span>{formatStamp(v.updated_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </article>
      </div>

      <p className="muted">
        Configure the Ollama URL and model under <Link to="/admin/llm">LLM connection</Link>. Head to{" "}
        <strong>Users</strong> to change plans or promote admins, or to <strong>Card data update</strong>{" "}
        to refresh the card database.
      </p>
    </>
  );
}
